// Per-account chat bubble colors (Settings → Account → Chat bubble colors).
// Colors are exposed as CSS variables in a dedicated <style> tag, not inline on
// <html>, because themeHelper.setColorTheme overwrites the html style attribute.
// Each configured bubble also adds a body class that switches on the themed
// rules in _conversation-view.scss, so accounts without a theme are untouched.

const STYLE_ELEMENT_ID = 'account-theme';
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const DARK_TEXT = '#1c2024';
const LIGHT_TEXT = '#ffffff';
// Read receipts must stay distinguishable from the delivered tick
const READ_ON_LIGHT_BUBBLE = '#18794e';
const READ_ON_DARK_BUBBLE = '#b1f1cb';

export const BUBBLE_THEMES = [
  { key: 'agent', setting: 'agent_bubble_color' },
  { key: 'bot', setting: 'bot_bubble_color' },
];

export const isHexColor = value =>
  typeof value === 'string' && HEX_COLOR.test(value);

const toLinear = channel => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

// WCAG relative luminance, 0 (black) to 1 (white)
export const relativeLuminance = hex => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

// Above ~0.179 black text has more contrast than white text
export const isLightColor = hex => relativeLuminance(hex) > 0.179;

export const contrastText = hex => (isLightColor(hex) ? DARK_TEXT : LIGHT_TEXT);

const bodyClass = key => `has-${key}-bubble-theme`;

export const buildThemeCss = (theme = {}) => {
  const declarations = BUBBLE_THEMES.filter(({ setting }) =>
    isHexColor(theme[setting])
  ).map(({ key, setting }) => {
    const bg = theme[setting];
    const read = isLightColor(bg) ? READ_ON_LIGHT_BUBBLE : READ_ON_DARK_BUBBLE;
    return [
      `--bubble-${key}-bg: ${bg};`,
      `--bubble-${key}-text: ${contrastText(bg)};`,
      `--bubble-${key}-read: ${read};`,
    ].join(' ');
  });
  return declarations.length ? `:root { ${declarations.join(' ')} }` : '';
};

export const applyAccountTheme = (theme = {}) => {
  const safeTheme = theme || {};
  BUBBLE_THEMES.forEach(({ key, setting }) => {
    document.body.classList.toggle(
      bodyClass(key),
      isHexColor(safeTheme[setting])
    );
  });

  const css = buildThemeCss(safeTheme);
  let styleElement = document.getElementById(STYLE_ELEMENT_ID);
  if (!css) {
    if (styleElement) styleElement.remove();
    return;
  }
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = STYLE_ELEMENT_ID;
    document.head.appendChild(styleElement);
  }
  styleElement.textContent = css;
};
